// src/pages/BookReader.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography, IconButton } from "@mui/material";
import { Canvas, PencilBrush, Line } from "fabric";

import { API_BASE, postJSON } from '../utils/api';
import "../styles/BookReader.css";
import { EventBus, TextLayerBuilder } from 'pdfjs-dist/web/pdf_viewer';
import 'pdfjs-dist/web/pdf_viewer.css';

import CommentSidebar from "../components/comments/CommentSidebar";
import NotesSidebar from "../components/notes/NotesSidebar";
import DrawingToolbar from "../components/pdf-reader/DrawingToolbar";
import ZoomControls from "../components/pdf-reader/ZoomControls";
import PageControls from "../components/pdf-reader/PageControls";
import { getDocumentMetadata } from "../utils/pdfUtils";
import { handwritingApi } from "../utils/readerApi";
import { serializeForPersist, loadAndNormalize } from "../utils/fabricSetup";


const MAX_ZOOM = 2.0;
const MIN_ZOOM = 0.5;
const CSS_DPI = 96;
const PAGE_PRESET = { widthIn: 8.5, heightIn: 11.0 }; // Letter
const LETTER_RATIO = PAGE_PRESET.heightIn / PAGE_PRESET.widthIn;
const STANDARD_WIDTH_PX = Math.round(PAGE_PRESET.widthIn * CSS_DPI);



/* stable container width to avoid first-paint jank */
function useSettledWidth(ref, minWidth = 320, debounceMs = 120) {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let raf = 0, t = 0;
    const commit = (x) => setW(Math.max(minWidth, Math.floor(x || 0)));
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? el.clientWidth ?? 0;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (t) clearTimeout(t);
        t = setTimeout(() => commit(width), debounceMs);
      });
    });
    ro.observe(el);
    raf = requestAnimationFrame(() => commit(el.clientWidth || 0));
    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      if (t) clearTimeout(t);
    };
  }, [ref, minWidth, debounceMs]);
  return w;
}


export default function BookReader() {
  /* role caps */
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user")) || null; } catch { return null; }
  }, []);
  const role = user?.role || "reader";
  // `roles` is the array the backend checks; `role` is a legacy singular mirror
  // that is not always populated, so reading it alone disagrees with the server.
  const isAdminViewer = Boolean(
    (Array.isArray(user?.roles) && user.roles.includes("admin")) || role === "admin"
  );
  const caps = useMemo(() => {
    if (["admin", "lead_reviewer", "reviewer", "author"].includes(role)) {
      return { comments: true, notes: true, drawing: true };
    }
    // Reader defaults
    return { comments: true, notes: true, drawing: false };
  }, [role]);
  

  /* routing / url */
const params = useParams();                 // e.g. /read/:bookId or /read/:id
const routeId = params.id || params.bookId || "";
const { state, search } = useLocation();
const passedBook = state?.book || null;

// parse query params once
const qs = new URLSearchParams(search);
const kind = (qs.get("type") || "").toLowerCase();  // "book" or "draft"
const querySrc = qs.get("src");
const preview = qs.get("preview");                  // "1", "true", etc.
const [penWidth, setPenWidth] = useState(2);
const [highlightWidth, setHighlightWidth] = useState(15);
const [eraserWidth, setEraserWidth] = useState(12);       // kept for UI; erase still object-delete
const [eraserStyle, setEraserStyle] = useState('erase');  // 'erase' | 'strike'
const [highlightMode, setHighlightMode] = useState('freehand'); // 'freehand' | 'text'

// stable key used elsewhere (keep this!)
const draftId = passedBook?._id || routeId || "unknown";

// final URL priority:
// 1) explicit passedBook.pdfUrl
// 2) ?src override
// 3) backend: /api/books/:id/pdf for books; /api/drafts/:id/pdf[?preview=1] for drafts
const rid = routeId || passedBook?.id || passedBook?._id || "";  // robust id fallback
const kraw = (qs.get("type") || "").toLowerCase();
const inferredKind =
  kraw === "book" || kraw === "draft"
    ? kraw
    : passedBook?.pdfUrl
      ? (passedBook.pdfUrl.includes("/drafts/") ? "draft" : "book")
      : "book"; // safe default

// final URL priority:
// 1) explicit passedBook.pdfUrl
// 2) ?src override
// 3) backend: /api/published/:id/pdf for books; /api/drafts/:id/pdf[?preview=1] for drafts
let pdfUrl =
  passedBook?.pdfUrl ||
  (querySrc
    ? decodeURIComponent(querySrc)
    : rid
      ? `${API_BASE}/api/${inferredKind === "book" ? "published" : "drafts"}/${rid}/pdf${
          inferredKind === "book" ? "" : (preview ? `?preview=${encodeURIComponent(preview)}` : "")
        }`
      : "");

// If this is a draft URL, ensure ?uid=<userId> is present so the iframe request is authorized.
if (pdfUrl && inferredKind !== "book") {
  const uid = user?.id || user?._id;
  if (uid && !/[\?&]uid=/.test(pdfUrl)) {
    pdfUrl += (pdfUrl.includes("?") ? "&" : "?") + `uid=${encodeURIComponent(uid)}`;
  }
}

  /* refs / state */
  const shellRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const fabricCanvasEl = useRef(null);
  const heatmapLayerRef = useRef(null);            // NEW
const [commentsSnapshot, setCommentsSnapshot] = useState([]);
  const renderTaskRef = useRef(null);
  const pdfRef = useRef(null);
  const textLayerRef = useRef(null);
  const marqueeLayerRef = useRef(null); // transparent layer that captures drag-rectangle during quote-pick
  const eventBusRef = useRef(new EventBus());
   // === Fixed-viewport zoom ===
  const pageBoxRef = useRef(null);        // fixed-size viewport container
  const pageContentRef = useRef(null);    // grows/shrinks with zoom
  const basePageRef = useRef({ w: 0, h: 0, page: 0 }); // base (100%) page size in px

  // grab-to-pan inside the viewport
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, left: 0, top: 0 });

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPage] = useState(1);
  const [zoomLevel, setZoom] = useState(1);
  const [isLoading, setLoading] = useState(true);
  const [loadError, setError] = useState(null);
  const [fabricReady, setFabOk] = useState(false);

  // --- Reader progress tracking (published books only) → author analytics.
  // Records reading status so "Active readers" / "Completion rate" work.
  const lastProgressRef = useRef('');
  useEffect(() => {
    if (inferredKind !== 'book' || !rid || !numPages) return;
    const key = `${rid}:${pageNumber}/${numPages}`;
    if (key === lastProgressRef.current) return; // dedupe repeat posts
    lastProgressRef.current = key;
    postJSON(`/api/books/${rid}/progress`, {
      page: pageNumber,
      totalPages: numPages,
    }).catch(() => {}); // progress is best-effort; never disrupt reading
  }, [inferredKind, rid, pageNumber, numPages]);

const savedToolRef = useRef(null);
const savedPointerRef = useRef("");

const [composing, setComposing] = useState(false);
const commentQuoteAppendRef = useRef(null);
const noteQuoteAppendRef = useRef(null);          // composer will register later
const quoteListenersRef = useRef({ onKey: null, onMouseUp: null, onMove: null, onSel: null });
const lastFrozenRangeRef = useRef(null);
const lastMouseRef = useRef({ x: 0, y: 0 });
const savedUpperPtrRef = useRef("");
const savedLowerVisRef = useRef("");
const savedUpperVisRef = useRef("");
const [showComments, setShowComments] = useState(false);
const [showNotes, setShowNotes] = useState(false);
const [autoOpenComposerTick, setAutoOpenComposerTick] = useState(0);
const [showCommentComposer, setShowCommentComposer] = useState(false);
const eraserHandlers = useRef({ onDown: null, onMove: null, onUp: null, onClick: null, onCreated: null, preview: null });

/* ---------- Cursor helpers (SVG → data URL) ---------- */
function makeCursor(svg, x, y) {
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${x} ${y}, auto`;
}

const PENCIL_CURSOR = () => makeCursor(`
  <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
    <g fill='none' stroke='#111' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
      <path d='M6 22 L22 6 L25 9 L9 25 Z' fill='#f2f2f2'/>
      <path d='M6 22 L9 25 L4 26 Z' fill='#cfcfcf' stroke='none'/>
    </g>
  </svg>
`, 4, 24);

const ERASER_CURSOR = () => makeCursor(`
  <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
    <g fill='none' stroke='#111' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
      <rect x='6' y='12' width='16' height='10' rx='3' ry='3' transform='rotate(-25 14 17)' fill='#f2f2f2'/>
      <path d='M10 23 L17 21' />
    </g>
  </svg>
`, 10, 20);

const MARKER_CURSOR = () => makeCursor(`
  <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
    <g fill='none' stroke='#111' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
      <path d='M7 22 L20 9 L23 12 L10 25 Z' fill='#f2f2f2'/>
      <path d='M20 9 L23 12' />
      <path d='M7 22 L10 25' />
    </g>
  </svg>
`, 5, 23);
const eraserWidthRef = useRef(12);
useEffect(() => { eraserWidthRef.current = eraserWidth; }, [eraserWidth]);

const [activeTool, setActiveTool] = useState(null);
const [color, setColor] = useState("#000000");
// undo/redo availability
const [canUndo, setCanUndo] = useState(false);
const [canRedo, setCanRedo] = useState(false);
const savingTimerRef = useRef(null);
const lastPersistRef = useRef(null); // remember last saved payload to avoid redundant PUTs
const lastCanvasSizeRef = useRef({ w: 0, h: 0 });
function registerCommentQuoteAppend(fn) {
  commentQuoteAppendRef.current = fn;
}
function registerNoteQuoteAppend(fn) {
  noteQuoteAppendRef.current = fn;
}

function onComposeOpen() {
  setComposing(true);
  const fEl = fabricCanvasEl.current;
  if (fEl) {
    fEl.style.visibility = "hidden";
    if (!quotePicking) fEl.style.pointerEvents = "";
  }
}

function onComposeClose() {
  setComposing(false);
  const fEl = fabricCanvasEl.current;
  if (fEl && !quotePicking) {
    fEl.style.visibility = "";
    fEl.style.pointerEvents = "";
    refreshFabric(fEl.fabric);   // <— ensure strokes repaint
  }
}


/* quote-picking session: pause drawing & overlay events */
const [quotePicking, setQuotePicking] = useState(false);
const [marqueeActive, setMarqueeActive] = useState(false);
const [selectionMenu, setSelectionMenu] = useState({
  open: false,
  x: 0,
  y: 0,
  data: null,  // { text, page, rects }
});

useEffect(() => {
  const el = fabricCanvasEl.current;
  const f = el?.fabric;
  const shouldHide = showComments || showNotes || composing || quotePicking;
  if (!el || !f) return;

  if (shouldHide) {
    f.isDrawingMode = false;
    f.selection = false;
    clearTransientHandlers(f);
    setActiveTool(null);
    hideOverlay(el, f);
  } else {
    showOverlay(el, f); // visible + zIndex + repaint
  }
}, [showComments, showNotes, composing, quotePicking]);


  const stageWidth = useSettledWidth(shellRef, 320, 120);

  const getStorageKey = (page) =>
    `revisionari_notes_${draftId}_${page}_${user?._id || user?.id || "anon"}_${role}`;

  /* ensure topbar var is set for layout padding */
  useLayoutEffect(() => {
    const nav = document.querySelector("nav, .navbar, #topbar");
    const setVars = () => {
      const nh = nav?.offsetHeight || 0;
      document.documentElement.style.setProperty("--rv-topbar-height", `${nh}px`);
    };
    setVars();
    window.addEventListener("resize", setVars);
    return () => window.removeEventListener("resize", setVars);
  }, []);



  /* load pdf + init fabric */
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError(null);

        if (pdfRef.current) { try { pdfRef.current.destroy(); } catch {} pdfRef.current = null; }

        if (!pdfUrl) throw new Error("No PDF URL.");
        const { pdf, numPages } = await getDocumentMetadata(pdfUrl);
        if (!pdf) throw new Error("PDF load failed");

        pdfRef.current = pdf;
        setNumPages(numPages || 1);
        setPage(1);

        if (fabricCanvasEl.current && !fabricCanvasEl.current.fabric) {
          const fab = new Canvas(fabricCanvasEl.current, {
            isDrawingMode: false,
            selection: false,
            backgroundColor: "transparent",
          });
          fab.renderOnAddRemove = true; // ensure repaint on add/remove

          fab.on("path:created", (e) => {
            const p = e?.path;
            if (p) {
              p.selectable = false;
              p.evented = false;
              p.hasControls = false;
              p.hasBorders = false;
              p.lockMovementX = true;
              p.lockMovementY = true;
              p.hoverCursor = 'default';
            }
            fab.discardActiveObject();
            fab.requestRenderAll();
            scheduleSave(400);
          });

          fabricCanvasEl.current.fabric = fab;
          // make the canvases explicitly visible and on top on first init
          showOverlay(fabricCanvasEl.current, fab);
          setFabOk(true);
        }
      } catch (err) {
        console.error(err);
        setError("Could not load PDF document.");
      } finally {
        setLoading(false);
      }
    };
    init();

    return () => {
      clearTimeout(savingTimerRef.current);
      // best effort flush before tear-down
      flushSave().finally(() => {
        if (pdfRef.current) { try { pdfRef.current.destroy(); } catch {} pdfRef.current = null; }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  /* pre-size sheet box to letter ratio */
  useLayoutEffect(() => {
    if (!stageWidth) return;
    const sheet = fabricCanvasEl.current?.parentNode; // .page-box
    if (!sheet) return;

    const targetWidth = Math.min(STANDARD_WIDTH_PX, stageWidth - 96); // a bit of side padding
    const targetHeight = Math.round(targetWidth * LETTER_RATIO);

    const prime = (c) => {
      if (!c) return;
      c.width = targetWidth;
      c.height = targetHeight;
      c.style.width = `${targetWidth}px`;
      c.style.height = `${targetHeight}px`;
    };

    sheet.style.width = `${targetWidth}px`;
    sheet.style.height = `${targetHeight}px`;
    prime(pdfCanvasRef.current);
    prime(fabricCanvasEl.current);
  }, [stageWidth]);

  /* scroll to top on width settle or page change */
  useEffect(() => {
    if (stageWidth >= 320) window.scrollTo({ top: 0, behavior: "auto" });
  }, [stageWidth, pageNumber]);

  /* render current page and sync fabric overlay */
  useEffect(() => {
    const doc = pdfRef.current;
    if (!doc || !pdfCanvasRef.current || !fabricCanvasEl.current) return;
    if (!stageWidth || stageWidth < 320) return;

    const availableWidth = stageWidth - 96;
    const targetWidth = Math.min(STANDARD_WIDTH_PX, availableWidth);

    doc
      .getPage(pageNumber)
      .then((page) => {
        const unscaled = page.getViewport({ scale: 1 });
        const baseScale = targetWidth / unscaled.width;
        const scale = baseScale * zoomLevel;
        const viewport = page.getViewport({ scale });
        // Base (100%) size for this page: fit-to-width at zoom 1
        const baseW = Math.round(unscaled.width  * baseScale);
        const baseH = Math.round(unscaled.height * baseScale);

        // If first time on this page, lock the viewport (page-box) size once
        if (basePageRef.current.page !== pageNumber) {
          basePageRef.current = { w: baseW, h: baseH, page: pageNumber };
          if (pageBoxRef.current) {
            pageBoxRef.current.style.width  = `${baseW}px`;
            pageBoxRef.current.style.height = `${baseH}px`;
          }
        }

        const bmp = pdfCanvasRef.current;
        const ctx = bmp.getContext("2d", { willReadFrequently: true });
        bmp.width = viewport.width;
        bmp.height = viewport.height;
        bmp.style.width = `${viewport.width}px`;
        bmp.style.height = `${viewport.height}px`;
        bmp.style.zIndex = '1'; 

        const ov = fabricCanvasEl.current;
        ov.width = viewport.width;
        ov.height = viewport.height;
        ov.style.width = `${viewport.width}px`;
        ov.style.height = `${viewport.height}px`;

        // Reset history when page changes so undo/redo is per-page.
        const fForHist = fabricCanvasEl.current.fabric;
        if (fForHist && fForHist.__rv_hist_page !== pageNumber) {
          ensureHistory(fForHist);
          fForHist.__rv_hist_init__ = false;  // force re-init after this render/load
          fForHist.__rv_hist_page = pageNumber;
        }

        const sheet = ov.parentNode;
        if (sheet) {
          sheet.style.width = `${viewport.width}px`;
          sheet.style.height = `${viewport.height}px`;
        }
        const textDiv = textLayerRef.current;
        if (textDiv) {
          textDiv.style.position = 'absolute';
          textDiv.style.left = '0';
          textDiv.style.top = '0';
          textDiv.style.width = `${viewport.width}px`;
          textDiv.style.height = `${viewport.height}px`;
          textDiv.style.zIndex = '2';           // <— text layer below fabric, above bitmap
          textDiv.innerHTML = ''; // clear previous text

          page.getTextContent().then((textContent) => {
            const textLayer = new TextLayerBuilder({
              textLayerDiv: textDiv,
              pageIndex: page.pageNumber - 1,
              viewport,
              eventBus: eventBusRef.current,
            });
            textLayer.setTextContent(textContent);
            textLayer.render();
          }).catch((e) => console.error('text layer error', e));
        }

        if (renderTaskRef.current) renderTaskRef.current.cancel();
        renderTaskRef.current = page.render({ canvasContext: ctx, viewport });

        renderTaskRef.current.promise
          .then(() => {
            const f = fabricCanvasEl.current.fabric;
            if (!f) return;

            // Always sync canvas dims first
            f.setDimensions({ width: viewport.width, height: viewport.height });
            f.calcOffset();

            /* Keep drawings locked to the PDF zoom.
              basePageRef.current.w is the 100% width we saved for this page.
              The current page width is viewport.width, so z = viewport.width / base.w == zoomLevel. */
            const base = basePageRef.current;
            if (base && base.w) {
              const z = Math.max(0.1, viewport.width / base.w);
              if (typeof f.setZoom === 'function') {
                f.setZoom(z);
              } else {
                // fallback for older Fabric builds
                f.setViewportTransform([z, 0, 0, z, 0, 0]);
              }
              f.requestRenderAll();
              }

            // Try server → fallback to localStorage → else clear
            (async () => {
              const loadFromLocal = async () => {
                const key = getStorageKey(pageNumber);
                const local = localStorage.getItem(key);
                if (!local) { f.clear(); return; }
                const parsed = JSON.parse(local);
                if (parsed && parsed.fabricJson && parsed.meta) {
                  // new format: { fabricJson, meta }
                  await loadAndNormalize(f, parsed);
                } else {
                  // legacy format: raw Fabric JSON (no meta)
                  await new Promise((resolve) => {
                    f.loadFromJSON(parsed, () => { f.renderAll(); resolve(); });
                  });
                }
              };

              try {
                const server = await handwritingApi.getPage({ bookId: rid, page: pageNumber });
                if (server && server.fabricJson) {
                  await loadAndNormalize(f, server);
                } else {
                  await loadFromLocal();
                }
              } catch {
                // network/server error -> local fallback
                await loadFromLocal();
              }

              if (!showComments && !showNotes && !composing && !quotePicking) {
                showOverlay(fabricCanvasEl.current, f);  // visible + zIndex + repaint
              } else {
                hideOverlay(fabricCanvasEl.current, f);  // keep it hidden while panels/picking
              }
              ensureHistory(f);
              if (!f.__rv_hist_init__) {
                f.__rv_history.undo = [ snapshotJSON(f) ];
                f.__rv_history.redo = [];
                f.__rv_hist_init__ = true;
                updateHistoryFlags(f);
                }

              // Attach persistence events once per canvas
              if (!f.__rv_persist_bound__) {
                f.__rv_persist_bound__ = true;
                f.on("path:created", (e) => {
                  const p = e?.path;
                  if (p) {
                    p.selectable = false;
                    p.evented = false;
                    p.hasControls = false;
                    p.hasBorders = false;
                    p.lockMovementX = true;
                    p.lockMovementY = true;
                    p.hoverCursor = 'default';
                  }
                  f.discardActiveObject();
                  f.requestRenderAll();
                  scheduleSave(400);

                  // Defer snapshot to the next tick so Fabric has fully committed the path.
                  // If the Pixel eraser's own handler asked us to skip this one, don't double-snapshot.
                  if (f.__rv_skip_next_snapshot) {
                    f.__rv_skip_next_snapshot = false;
                  } else {
                    setTimeout(() => pushSnapshot(f), 0);
                  }
                });
                f.on("object:modified", () => {
                  scheduleSave(600);
                  pushSnapshot(f);                 // <-- snapshot after edits
                });
                f.on("object:removed", () => {
                  scheduleSave(400);
                  pushSnapshot(f);                 // <-- snapshot after deletions
                });
              }
            })();
          })
          .catch((err) => {
            if (err?.name !== "RenderingCancelledException") console.error("Render error:", err);
          });
      })
      .catch((err) => console.error("getPage failed:", err));
  }, [pageNumber, zoomLevel, numPages, stageWidth]); // draftId not needed here

  // --- Fallback "brush eraser": deletes any overlay object whose bbox intersects the eraser disc as you drag.
function enableBrushEraser(f) {
  // draw as a normal freehand stroke, but store the path with gCO = 'destination-out'
  f.isDrawingMode = true;
  const brush = new PencilBrush(f);
  brush.width = eraserWidthRef.current;
  brush.color = '#000';                   // color is irrelevant when using destination-out
  f.freeDrawingBrush = brush;

  // temporary listener to tag the created path as an "eraser" stroke
  const onCreated = (e) => {
  const p = e.path;
  if (!p) return;
  p.globalCompositeOperation = 'destination-out';
  p.selectable = false;
  p.evented = false;
  p.hasControls = false;
  p.hasBorders = false;
  p.lockMovementX = true;
  p.lockMovementY = true;
  p.hoverCursor = 'default';
  f.discardActiveObject();
  f.requestRenderAll();
  scheduleSave(300);

  // Ensure exactly ONE snapshot for pixel eraser (after gCO is applied).
  f.__rv_skip_next_snapshot = true;     // tell the global handler to skip this cycle
  setTimeout(() => pushSnapshot(f), 0); // snapshot on next tick for correct order
  };

  f.on('path:created', onCreated);
  // keep reference so clearTransientHandlers() can remove it on tool switch
  eraserHandlers.current = { onDown: null, onMove: null, onUp: null, onClick: null, onCreated };
}

// --- Strike eraser (draws a straight segment)
function enableStrikeEraser(f, color, width) {
  let start = null;
  let preview = null;

  const removePreview = () => {
    if (preview) { try { f.remove(preview); } catch {} preview = null; }
  };

  const onDown = (e) => {
    start = f.getPointer(e.e);
    // faint dashed preview so user sees the strike
    preview = new Line([start.x, start.y, start.x, start.y], {
      stroke: color || '#000',
      strokeWidth: width || eraserWidthRef.current,
      opacity: 0.35,
      strokeDashArray: [6, 6],
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false
    });
    f.add(preview);
  };

  const onMove = (e) => {
    if (!start || !preview) return;
    const p = f.getPointer(e.e);
    preview.set({ x2: p.x, y2: p.y });
    f.requestRenderAll();
  };

  const onUp = (e) => {
    if (!start) return;
    const end = f.getPointer(e.e);
    const w = (width || eraserWidthRef.current) || 6;

    // Expanded AABB around the segment (quick corridor test)
    const minX = Math.min(start.x, end.x) - w;
    const minY = Math.min(start.y, end.y) - w;
    const maxX = Math.max(start.x, end.x) + w;
    const maxY = Math.max(start.y, end.y) + w;

    const toRemove = [];
    for (const obj of f.getObjects()) {
      if (!obj || obj === f.backgroundImage || obj === preview) continue;
      if (obj.type !== 'path' && obj.type !== 'line') continue;
      const b = obj.getBoundingRect(true, true);
      if (b.left < maxX && (b.left + b.width) > minX && b.top < maxY && (b.top + b.height) > minY) {
        toRemove.push(obj);
      }
    }

    if (toRemove.length) {
      // batch remove without spamming history
      ensureHistory(f);
      f.__rv_history.mute = true;
      toRemove.forEach(o => f.remove(o));
      f.__rv_history.mute = false;
      pushSnapshot(f);                      // one snapshot for the entire strike
    }

    removePreview();
    f.discardActiveObject();
    f.requestRenderAll();
    scheduleSave(200);

    start = null;
  };

  f.on('mouse:down', onDown);
  f.on('mouse:move', onMove);
  f.on('mouse:up',   onUp);

  eraserHandlers.current = { onDown, onMove, onUp, onClick: null, preview };
}

function clearTransientHandlers(f) {
  const h = eraserHandlers.current || {};
  if (h.onDown)    f.off('mouse:down', h.onDown);
  if (h.onMove)    f.off('mouse:move', h.onMove);
  if (h.onUp)      f.off('mouse:up',   h.onUp);
  if (h.onClick)   f.off('mouse:down', h.onClick);
  if (h.onCreated) f.off('path:created', h.onCreated);
  if (h.preview && h.preview instanceof Line) { try { f.remove(h.preview); } catch {} }

  f.isDrawingMode = false;
  f.freeDrawingBrush = null;
  f.freeDrawingCursor = 'default';
  f.defaultCursor = 'default';
  f.skipTargetFind = false;

  eraserHandlers.current = { onDown: null, onMove: null, onUp: null, onClick: null, onCreated: null, preview: null };
}

/** Ensure overlay is positively visible and layered above textLayer, then repaint. */
function showOverlay(el, f) {
  if (!el || !f) return;
  const upper = f.upperCanvasEl;
  // lower canvas
  el.style.visibility = 'visible';
  el.style.pointerEvents = 'auto';
  el.style.zIndex = '3';
  // canvas container (Fabric wraps lower+upper)
  const cont = el.parentElement;
  if (cont) {
    cont.style.visibility = 'visible';
    cont.style.pointerEvents = 'auto';
    cont.style.zIndex = '3';
  }
  // upper canvas (events layer)
  if (upper) {
    upper.style.visibility = 'visible';
    upper.style.pointerEvents = 'auto';
    upper.style.zIndex = '3';
  }
  refreshFabric(f);
}

/** Hide overlay explicitly (used when panels open). */
function hideOverlay(el, f) {
  if (!el || !f) return;
  const upper = f.upperCanvasEl;
  el.style.visibility = 'hidden';
  el.style.pointerEvents = 'none';
  const cont = el.parentElement;
  if (cont) { cont.style.visibility = 'hidden'; cont.style.pointerEvents = 'none'; }
  if (upper) { upper.style.visibility = 'hidden'; upper.style.pointerEvents = 'none'; }
}

/** Force Fabric to repaint (fix “invisible strokes until click/hover”). */
function refreshFabric(f) {
  if (!f) return;
  try {
    f.calcOffset();
    f.requestRenderAll();
  } catch {}
}
  // drawing tools
  const onToolSelect = (tool) => {
    const f = fabricCanvasEl.current?.fabric;
    if (!f) return;

    // Toggle OFF if clicking the same tool
    if (activeTool === tool) {
      clearTransientHandlers(f);
      setActiveTool(null);
      return;
    }

    // Fresh baseline before enabling a new tool
    clearTransientHandlers(f);
    f.off('mouse:down');
    f.off('mouse:up');
    f.off('mouse:move');
    f.isDrawingMode = false;
    f.selection = false;
    f.skipTargetFind = false;
    f.defaultCursor = 'default';

    switch (tool) {
    case 'pen': {
      f.isDrawingMode = true;
      f.freeDrawingBrush = new PencilBrush(f);
      f.freeDrawingBrush.color = color;
      f.freeDrawingBrush.width = penWidth;
      f.freeDrawingCursor = PENCIL_CURSOR();   // <— custom pencil cursor
      setActiveTool('pen');
      break;
    }

    case 'highlight': {
      if (highlightMode === 'text') {
        setActiveTool(null);
        beginNativeQuotePicking();
        break;
      }
      f.isDrawingMode = true;
      f.freeDrawingBrush = new PencilBrush(f);
      f.freeDrawingBrush.color = `${color}80`;
      f.freeDrawingBrush.width = highlightWidth;
      f.freeDrawingCursor = MARKER_CURSOR();   // <— custom marker cursor
      setActiveTool('highlight');
      break;
    }

    case 'eraser': {
      // clean baseline, then enable the chosen mode
      clearTransientHandlers(f);
      f.selection = false;
      f.skipTargetFind = true; // ignore hit-tests while erasing

      if (eraserStyle === 'strike') {
        f.isDrawingMode = false;
        f.freeDrawingCursor = 'default';
        f.defaultCursor = ERASER_CURSOR();            // <— eraser icon for strike
        enableStrikeEraser(f, color, eraserWidthRef.current);
      } else {
        f.defaultCursor = 'default';
        f.freeDrawingCursor = ERASER_CURSOR();        // <— eraser icon while drawing pixels
        enableBrushEraser(f);
      }

      setActiveTool('eraser');
      break;
    }

    default:
      setActiveTool(null);
  }
};


useEffect(() => {
  const f = fabricCanvasEl.current?.fabric;
  if (!f || !f.freeDrawingBrush) return;
  if (activeTool === 'pen') {
    f.freeDrawingBrush.color = color;
    f.freeDrawingBrush.width = penWidth;
  } else if (activeTool === 'highlight') {
    f.freeDrawingBrush.color = `${color}80`;
    f.freeDrawingBrush.width = highlightWidth;
  }
}, [activeTool, color, penWidth, highlightWidth]);

  // ===== NEW: persistence helpers =====
  function scheduleSave(delay = 600) {
    clearTimeout(savingTimerRef.current);
    savingTimerRef.current = setTimeout(() => flushSave(), delay);
  }

  async function flushSave() {
    const f = fabricCanvasEl.current?.fabric;
    if (!f) return;
    const w = f.getWidth(), h = f.getHeight();
    const payload = serializeForPersist(f, { baseScale: 1 });

    // Avoid redundant PUTs if JSON didn’t change
    const snap = JSON.stringify(payload || {});
    if (lastPersistRef.current === snap) return;

    try {
      await handwritingApi.savePage({
        bookId: rid,
        page: pageNumber,
        fabricJson: payload.fabricJson,
        meta: payload.meta,
      });
      lastPersistRef.current = snap;
      lastCanvasSizeRef.current = { w, h };
      // Also keep a local fallback
      localStorage.setItem(getStorageKey(pageNumber), JSON.stringify(payload));
    } catch (e) {
      // Keep local fallback even on failure
      localStorage.setItem(getStorageKey(pageNumber), JSON.stringify(payload));
      console.warn("PUT handwriting failed; kept local copy", e?.message || e);
    }
  }
// ===== UNDO / REDO =====
function snapshotJSON(f) {
  // include extra props we rely on (e.g., globalCompositeOperation)
  return JSON.stringify(
    f.toJSON([
      'globalCompositeOperation','selectable','evented','hasControls','hasBorders',
      'lockMovementX','lockMovementY','hoverCursor','strokeDashArray','opacity','stroke','strokeWidth','fill'
    ])
  );
}
function ensureHistory(f) {
  if (!f.__rv_history) f.__rv_history = { undo: [], redo: [], mute: false };
}
function updateHistoryFlags(f) {
  const H = f?.__rv_history;
  if (!H) { setCanUndo(false); setCanRedo(false); return; }
  setCanUndo(H.undo.length > 1);      // at least one prior state to go back to
  setCanRedo(H.redo.length > 0);
}
function pushSnapshot(f) {
  ensureHistory(f);
  const H = f.__rv_history;
  if (H.mute) return;
  const snap = snapshotJSON(f);
  // avoid duplicate push if identical
  if (H.undo.length === 0 || H.undo[H.undo.length - 1] !== snap) {
    H.undo.push(snap);
    H.redo.length = 0; // clear redo history on new change
    updateHistoryFlags(f);
  }
}
function doUndo() {
  const f = fabricCanvasEl.current?.fabric;
  if (!f || !f.__rv_history) return;
  const H = f.__rv_history;
  if (H.undo.length <= 1) return;           // nothing to undo
  const current = H.undo.pop();             // move current to redo
  H.redo.push(current);
  const prev = H.undo[H.undo.length - 1];   // load previous
  H.mute = true;
  f.loadFromJSON(prev, () => {
    H.mute = false;
    f.discardActiveObject();
    f.requestRenderAll();
    scheduleSave(300);
    updateHistoryFlags(f);
  });
}
function doRedo() {
  const f = fabricCanvasEl.current?.fabric;
  if (!f || !f.__rv_history) return;
  const H = f.__rv_history;
  if (H.redo.length === 0) return;
  const next = H.redo.pop();
  H.undo.push(next);
  H.mute = true;
  f.loadFromJSON(next, () => {
    H.mute = false;
    f.discardActiveObject();
    f.requestRenderAll();
    scheduleSave(300);
    updateHistoryFlags(f);
  });
}

// ---- Marquee helpers (geometry + text extraction) ----
function getPageBoxRect() {
  // Use the actually drawn page (scales with zoom), not the fixed viewport
  const el = pageContentRef.current || document.querySelector(".page-content");
  return el ? el.getBoundingClientRect() : { left: 0, top: 0, width: 1, height: 1 };
}
function toLocal(clientX, clientY, host) {
  return { x: clientX - host.left, y: clientY - host.top };
}
function rectFromPoints(a, b) {
  const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}
function intersects(a, b) {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}
function clamp(val, lo, hi) { return Math.max(lo, Math.min(hi, val)); }

// Build text + rects by intersecting the marquee with pdf.js .textLayer spans.
// Returns { text, rects } where rects are normalized (0..1) relative to page box.
function collectByMarquee(selRectPx, hostBox, textLayerEl) {
  const spans = Array.from(textLayerEl?.querySelectorAll("span") || []);
  if (!spans.length) return { text: "", rects: [] };

  const rectsSel = selRectPx; // {x,y,w,h} in px relative to host

  // hit test spans; keep their per-line rects that intersect the marquee
  const hits = [];
  for (const sp of spans) {
    const rlist = Array.from(sp.getClientRects());
    for (const cr of rlist) {
      const rr = { x: cr.left - hostBox.left, y: cr.top - hostBox.top, w: cr.width, h: cr.height };
      if (!intersects(rr, rectsSel)) continue;
      const t = (sp.textContent || "").trim();
      if (!t) continue;
      hits.push({ el: sp, rr });
    }
  }
  if (!hits.length) return { text: "", rects: [] };

  // de-duplicate by element (some spans yield multiple rects)
  const uniq = new Map();
  for (const h of hits) if (!uniq.has(h.el)) uniq.set(h.el, h);
  const list = Array.from(uniq.values());

  // sort top->bottom, then left->right (tolerance for same line)
  list.sort((a, b) => {
    const ar = a.el.getBoundingClientRect(), br = b.el.getBoundingClientRect();
    if (Math.abs(ar.top - br.top) > 2) return ar.top - br.top;
    return ar.left - br.left;
  });

  // concatenate text, normalize whitespace, cap later in Composer
  const text = list.map(h => (h.el.textContent || "")).join(" ").replace(/\s+/g, " ").trim();

  // build highlight rects: clip each span-rect to marquee and normalize
  const clipped = [];
  for (const h of hits) {
    const rr = h.rr;
    const x1 = clamp(Math.max(rr.x, rectsSel.x), 0, hostBox.width);
    const y1 = clamp(Math.max(rr.y, rectsSel.y), 0, hostBox.height);
    const x2 = clamp(Math.min(rr.x + rr.w, rectsSel.x + rectsSel.w), 0, hostBox.width);
    const y2 = clamp(Math.min(rr.y + rr.h, rectsSel.y + rectsSel.h), 0, hostBox.height);
    if (x2 <= x1 || y2 <= y1) continue;
    clipped.push({
      x: (x1) / hostBox.width,
      y: (y1) / hostBox.height,
      w: (x2 - x1) / hostBox.width,
      h: (y2 - y1) / hostBox.height,
    });
  }
  return { text, rects: clipped };
}

// ---- Heatmap helpers (comments -> gold overlays) ----
// geometry helper used by rebuildHeatmap
const _intersects = (a, b) =>
  !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);

// Paint absolute rects (in px) into the overlay
function paintHeatmap(rectsPx = []) {
  const host = heatmapLayerRef.current;
  if (!host) return;
  host.innerHTML = "";

  const frag = document.createDocumentFragment();
  for (const r of rectsPx) {
    const el = document.createElement("div");
    el.className = "heat-rect";
    el.style.left   = r.x + "px";
    el.style.top    = r.y + "px";
    el.style.width  = r.w + "px";
    el.style.height = r.h + "px";
    el.style.setProperty("--alpha", (r.alpha ?? 0.25).toString());
    frag.appendChild(el);
  }
  host.appendChild(frag);
}

// Rebuild heatmap from normalized rects saved with comments.highlight.segments
function rebuildHeatmap() {
  if (!showComments) { paintHeatmap([]); return; }

  const pageEl = pageContentRef.current;
  const textEl = textLayerRef.current;
  const host   = heatmapLayerRef.current;
  if (!pageEl || !textEl || !host) return;

  // Use the actual drawn page size (what the user sees right now)
  const pageBox = pageEl.getBoundingClientRect();
  const W = Math.max(1, pageBox.width);
  const H = Math.max(1, pageBox.height);

  // 1) Collect normalized rects for this page + count duplicates for intensity
  const normRects = [];
  const counts = new Map();
  (commentsSnapshot || []).forEach((c) => {
    const segs = c?.highlight?.segments || [];
    segs.forEach((s) => {
      if (s?.page !== pageNumber) return;
      (s.rects || []).forEach((r) => {
        if (typeof r.x !== "number") return;
        const key = `${Math.round(r.x*1000)}:${Math.round(r.y*1000)}:${Math.round(r.w*1000)}:${Math.round(r.h*1000)}`;
        counts.set(key, (counts.get(key) || 0) + 1);
        normRects.push({ ...r, _key: key });
      });
    });
  });

  if (!normRects.length) { paintHeatmap([]); return; }

  // 2) Rough px rects in page-content coords
  const approxPx = normRects.map((r) => ({
    x: r.x * W, y: r.y * H, w: r.w * W, h: r.h * H, _key: r._key,
  }));

  // 3) SNAP to the current text spans: intersect with span client rects
  const spanRects = [];
  for (const sp of textEl.querySelectorAll("span")) {
    for (const cr of sp.getClientRects()) {
      spanRects.push({
        x: cr.left - pageBox.left,
        y: cr.top  - pageBox.top,
        w: cr.width,
        h: cr.height,
      });
    }
  }

  const snapped = [];
  for (const r of approxPx) {
    for (const s of spanRects) {
      if (!_intersects(r, s)) continue;
      const x1 = Math.max(r.x, s.x);
      const y1 = Math.max(r.y, s.y);
      const x2 = Math.min(r.x + r.w, s.x + s.w);
      const y2 = Math.min(r.y + r.h, s.y + s.h);
      if (x2 > x1 && y2 > y1) {
        snapped.push({ x: x1, y: y1, w: x2 - x1, h: y2 - y1, _key: r._key });
      }
    }
  }

  if (!snapped.length) { paintHeatmap([]); return; }

  const max = Math.max(...counts.values());
  const out = snapped.map((r) => ({
    ...r,
    alpha: 0.18 + 0.22 * ((counts.get(r._key) || 1) / max), // 0.18–0.40
  }));

  // 4) Size overlay and paint
  host.style.width  = `${W}px`;
  host.style.height = `${H}px`;
  paintHeatmap(out);
}




// Rebuild when panel/page/zoom/data change
useEffect(() => {
  rebuildHeatmap();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showComments, pageNumber, zoomLevel, commentsSnapshot]);
useEffect(() => {
  // Helper to write CSS variables
  const setVar = (name, px) =>
    document.documentElement.style.setProperty(name, `${Math.max(0, Math.round(px))}px`);

  // Try common footer selectors; adjust if your footer uses a different class
  const footerEl =
    document.querySelector('footer') ||
    document.querySelector('.app-footer') ||
    document.querySelector('.site-footer');

  // Our bottom controls cluster
  const controlsEl = document.querySelector('.bottom-center-controls');

  const measure = () => {
    const fh = footerEl ? footerEl.getBoundingClientRect().height : 0;
    const ch = controlsEl ? controlsEl.getBoundingClientRect().height : 0;
    setVar('--rv-footer-h', fh || 72);   // sensible fallbacks
    setVar('--rv-controls-h', ch || 64);
  };

  // Initial measure + on resize
  measure();
  const onResize = () => measure();
  window.addEventListener('resize', onResize);

  // Keep in sync if either element resizes
  const ro = new ResizeObserver(measure);
  footerEl && ro.observe(footerEl);
  controlsEl && ro.observe(controlsEl);

  return () => {
    window.removeEventListener('resize', onResize);
    ro.disconnect();
  };
}, []);

function beginQuotePicking() {
  const fEl = fabricCanvasEl.current;
  const f = fEl?.fabric;
  if (!fEl || !f) return;

  // reset any prior menu
  setSelectionMenu({ open: false, x: 0, y: 0, data: null });

  window.__quotePicking = true;
  setQuotePicking(true);
  setMarqueeActive(true);

  // mark page for CSS overrides (disable native selection/callout)
  const pageEl = marqueeLayerRef.current?.parentElement; // .page-box
  pageEl?.classList.add('quote-picking');

  // remember current drawing state
  savedToolRef.current   = activeTool;
  savedPointerRef.current = fEl.style.pointerEvents || "";
  savedLowerVisRef.current = fEl.style.visibility || "";
  savedUpperPtrRef.current = f.upperCanvasEl?.style.pointerEvents || "";
  savedUpperVisRef.current = f.upperCanvasEl?.style.visibility || "";

  // fully pause Fabric (no events, no visuals while composing/picking)
  f.isDrawingMode = false;
  f.selection = false;
  clearTransientHandlers(f);
  setActiveTool(null);
  fEl.style.pointerEvents = "none";
  f.upperCanvasEl.style.pointerEvents = "none";
  fEl.style.visibility = "hidden";
  f.upperCanvasEl.style.visibility = "hidden";

  // clear any native selection to avoid blue handles / context UI
  try {
    const sel = window.getSelection?.();
    sel && sel.removeAllRanges && sel.removeAllRanges();
  } catch {}

  // prepare marquee layer
  const layer = marqueeLayerRef.current;
  if (layer) {
    layer.innerHTML = "";
    layer.style.pointerEvents = "auto";
    layer.style.cursor = "crosshair";
    // kill native context menu while picking
    const preventMenu = (e) => e.preventDefault();
    layer.addEventListener('contextmenu', preventMenu, true);
    quoteListenersRef.current.onMenu = { el: layer, fn: preventMenu };
  }

  const hostBox = getPageBoxRect();
  let start = null, boxEl = null;

  const drawBox = (rect) => {
    if (!boxEl) {
      boxEl = document.createElement("div");
      boxEl.className = "marquee-box";
      layer.appendChild(boxEl);
    }
    boxEl.style.left = `${rect.x}px`;
    boxEl.style.top = `${rect.y}px`;
    boxEl.style.width = `${rect.w}px`;
    boxEl.style.height = `${rect.h}px`;
  };

  // Use Pointer Events so Chrome/Edge & touch are consistent
  const onPointerDown = (ev) => {
    if (!window.__quotePicking) return;
    layer.setPointerCapture?.(ev.pointerId);
    const p = ev;
    start = toLocal(p.clientX, p.clientY, hostBox);
    drawBox(rectFromPoints(start, start));
    document.addEventListener("pointermove", onPointerMove, true);
    document.addEventListener("pointerup", onPointerUp, true);
    ev.preventDefault();
  };
  const onPointerMove = (ev) => {
    if (!start) return;
    const cur = toLocal(ev.clientX, ev.clientY, hostBox);
    drawBox(rectFromPoints(start, cur));
  };
  const onPointerUp = () => {
    document.removeEventListener("pointermove", onPointerMove, true);
    document.removeEventListener("pointerup", onPointerUp, true);
    if (!start || !boxEl) return;

    const rect = {
      x: parseFloat(boxEl.style.left),
      y: parseFloat(boxEl.style.top),
      w: parseFloat(boxEl.style.width),
      h: parseFloat(boxEl.style.height),
    };
    const { text, rects } = collectByMarquee(rect, hostBox, textLayerRef.current);
    if (!text) return; // nothing usable

    // place the small context menu near the rectangle's bottom-right
    const x = clamp(rect.x + rect.w + 6, 0, hostBox.width - 180);
    const y = clamp(rect.y + rect.h + 6, 0, hostBox.height - 60);

    setSelectionMenu({
      open: true,
      x,
      y,
      data: { text, page: pageNumber, rects },
    });
  };

  // ESC to cancel
  const onKey = (e) => {
    if (e.key === 'Escape') {
      endQuotePicking();
    }
  };

  layer.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("keydown", onKey, true);

  // remember listeners for cleanup
  quoteListenersRef.current.onPointerDown = onPointerDown;
  quoteListenersRef.current.onKey = onKey;
}



/**
 * beginNativeQuotePicking
 * Unified entry used by BOTH:
 *  - “Write a comment” FAB
 *  - Composer’s “Highlight Quote” button
 *
 * Flow:
 *  - Pause Fabric (hide upper/lower canvases, disable events)
 *  - DO NOT block native selection; the user selects exact text in .textLayer
 *  - On mouseup (handled inside pickOneQuoteSelection), we compute menu position
 *  - Show the “Paste / Unselect” menu near the selection
 *  - Quote stays in picking mode so user can repeat until composer finishes
 */
function beginNativeQuotePicking() {
  const fEl = fabricCanvasEl.current;
  const f = fEl?.fabric;
  if (!fEl || !f) return;

  // reset any prior context menu
  setSelectionMenu({ open: false, x: 0, y: 0, data: null });

  // mark global picking state
  window.__quotePicking = true;
  setQuotePicking(true);

  // DO NOT add .quote-picking class; we want native selection active

  // remember current drawing state (so we can restore later)
  savedToolRef.current    = activeTool;
  savedPointerRef.current = fEl.style.pointerEvents || "";
  savedLowerVisRef.current = fEl.style.visibility || "";
  savedUpperPtrRef.current = f.upperCanvasEl?.style.pointerEvents || "";
  savedUpperVisRef.current = f.upperCanvasEl?.style.visibility || "";

  // fully pause Fabric (no events, no visuals while composing/picking)
  f.isDrawingMode = false;
  f.selection = false;
  clearTransientHandlers(f);
  setActiveTool(null);
  fEl.style.pointerEvents = "none";
  f.upperCanvasEl.style.pointerEvents = "none";
  fEl.style.visibility = "hidden";
  f.upperCanvasEl.style.visibility = "hidden";

  // ensure the marquee overlay does NOT eat events in native flow
  const layer = marqueeLayerRef.current;
  if (layer) {
    layer.innerHTML = "";
    layer.style.pointerEvents = "none";
    layer.style.cursor = "";
  }

  // Wait for one native selection, then open our menu
  pickOneQuoteSelection()
    .then(({ text, page, rects }) => {
      if (!text || !rects?.length) return;
      const host = getPageBoxRect();
      // place menu just below the last rect (clamped)
      const r = rects[rects.length - 1] || rects[0];
      const x = clamp(r.x * host.width, 6, host.width - 180);
      const y = clamp((r.y + r.h) * host.height + 6, 0, host.height - 80);

      setSelectionMenu({
        open: true,
        x,
        y,
        data: { text, page, rects },
      });
    })
    .catch(() => {
      // cancelled via ESC or no selection; do nothing (still in picking mode)
    });
}



function endQuotePicking() {
  setSelectionMenu({ open: false, x: 0, y: 0, data: null });

  window.__quotePicking = false;
  setQuotePicking(false);
  setMarqueeActive(false);

  // remove listeners
  const L = quoteListenersRef.current || {};
  const layer = marqueeLayerRef.current;
  if (layer && L.onPointerDown) layer.removeEventListener("pointerdown", L.onPointerDown, true);
  if (L.onKey) document.removeEventListener("keydown", L.onKey, true);
  if (L.onMenu?.el && L.onMenu?.fn) L.onMenu.el.removeEventListener("contextmenu", L.onMenu.fn, true);
  quoteListenersRef.current = { onKey: null, onMouseUp: null, onMove: null, onSel: null };

  // clear marquee visuals
  if (layer) {
    layer.style.pointerEvents = "none";
    layer.style.cursor = "";
    layer.innerHTML = "";
  }

  // remove CSS guard that disabled native selection
  const pageEl = layer?.parentElement;
  pageEl?.classList.remove('quote-picking');

  // restore Fabric canvases (still hidden if composer open)
  const fEl = fabricCanvasEl.current;
  const f   = fEl?.fabric;
  if (fEl) {
    fEl.style.pointerEvents = savedPointerRef.current || "";
    fEl.style.visibility    = composing ? "hidden" : (savedLowerVisRef.current || "");
  }
  if (f?.upperCanvasEl) {
    f.upperCanvasEl.style.pointerEvents = savedUpperPtrRef.current || "";
    f.upperCanvasEl.style.visibility    = composing ? "hidden" : (savedUpperVisRef.current || "");
    f.upperCanvasEl.style.cursor        = "";
  }
  savedPointerRef.current = "";
  savedUpperPtrRef.current = "";
  savedLowerVisRef.current = "";
  savedUpperVisRef.current = "";

  // make sure existing strokes repaint as soon as canvases are visible
  if (f) refreshFabric(f);

  // optionally restore previous tool (if not composing)
  const prev = savedToolRef.current;
  savedToolRef.current = null;
  if (prev && !composing) onToolSelect(prev);
}



function onPasteQuoteFromMenu() {
  const p = selectionMenu.data;
  if (p) {
    const fn = showComments
      ? commentQuoteAppendRef.current
      : (showNotes ? noteQuoteAppendRef.current : null);
    fn?.(p);
  }

  // Clear native selection so the blue highlight disappears after paste.
  const sel = (document.getSelection && document.getSelection()) || window.getSelection?.();
  try { sel && sel.removeAllRanges && sel.removeAllRanges(); } catch {}

  setSelectionMenu({ open: false, x: 0, y: 0, data: null });
}
function onUnselectFromMenu() {
  setSelectionMenu({ open: false, x: 0, y: 0, data: null });
}

function onCursorCommentClick() {
  setShowNotes(false);
  setShowComments(true);
  setShowCommentComposer(true);
  beginNativeQuotePicking();
}
// Let the user select a quote; return rects normalized to the drawn page (.page-content)
const pickOneQuoteSelection = () =>
  new Promise((resolve, reject) => {
    console.log("Reader:pickOneQuoteSelection:init");

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      document.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("keydown", onKey, true);
    };

    const onKey = (e) => {
      if (e.key === "Escape") {
        console.log("Reader:pickOneQuoteSelection:esc");
        cleanup();
        reject(new Error("Selection cancelled"));
      }
    };

    const onMouseUp = () => {
      const sel = window.getSelection();
      console.log("Reader:pickOneQuoteSelection:mouseup raw =", sel?.toString());

      const text = sel && sel.toString().trim();
      if (!sel || !sel.rangeCount || !text) {
        console.log("Reader:pickOneQuoteSelection:mouseup no usable text");
        return; // wait for real selection
      }

      const range = sel.getRangeAt(0);

      // IMPORTANT: normalize against the scaled page element
      const pageEl = pageContentRef?.current;
      const fallbackBox = document.querySelector(".page-content") || document.querySelector(".page-box");
      const hostEl =
        range.startContainer?.parentElement?.closest(".page-content") ||
        pageEl ||
        fallbackBox ||
        document.body;

      const host = hostEl.getBoundingClientRect();
      const hostW = Math.max(1, host.width);
      const hostH = Math.max(1, host.height);

      let rects = Array.from(range.getClientRects()).map((r) => ({
        x: (r.left - host.left) / hostW,
        y: (r.top  - host.top)  / hostH,
        w:  r.width              / hostW,
        h:  r.height             / hostH,
      }));

      // Filter noise & clamp to [0..1]
      rects = rects
        .filter((r) => r.w > 0.002 && r.h > 0.002)
        .map((r) => {
          const x = Math.max(0, Math.min(1, r.x));
          const y = Math.max(0, Math.min(1, r.y));
          const w = Math.max(0, Math.min(1 - x, r.w));
          const h = Math.max(0, Math.min(1 - y, r.h));
          return { x, y, w, h };
        });

      try { sel.removeAllRanges(); } catch {}

      console.log("Reader:pickOneQuoteSelection:resolve", { len: text.length, page: pageNumber, rects: rects.length });
      cleanup();
      resolve({ text, page: pageNumber, rects });
    };

    document.addEventListener("mouseup", onMouseUp, true);
    document.addEventListener("keydown", onKey, true);
  });

// === Grab-to-pan inside the fixed viewport (.page-box) ===
const handlePanStart = (e) => {
  if (e.button !== 0) return; // left-click only
  // ignore drags that start on UI chrome
  if (e.target.closest?.('.bottom-center-controls, .toolbar-wrapper, .reader-fabs, button, a')) return;

  const view = pageBoxRef.current;
  if (!view) return;

  setIsPanning(true);
  view.classList.add('grabbing');
  panStartRef.current = {
    x: e.clientX,
    y: e.clientY,
    left: view.scrollLeft,
    top: view.scrollTop,
  };

  window.addEventListener('mousemove', handlePanMove);
  window.addEventListener('mouseup', handlePanEnd);
};

const handlePanMove = (e) => {
  if (!isPanning) return;
  const view = pageBoxRef.current;
  if (!view) return;
  const dx = e.clientX - panStartRef.current.x;
  const dy = e.clientY - panStartRef.current.y;
  view.scrollLeft = panStartRef.current.left - dx;
  view.scrollTop  = panStartRef.current.top  - dy;
};

const handlePanEnd = () => {
  setIsPanning(false);
  const view = pageBoxRef.current;
  if (view) view.classList.remove('grabbing');
  window.removeEventListener('mousemove', handlePanMove);
  window.removeEventListener('mouseup', handlePanEnd);
};


  // Trackpad-like zoom that keeps the viewed center in place
const zoomTo = (next) => {
  next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));

  // keep the visual center in place while zooming
  const pageEl = document.querySelector('.page-box');
  const rect = pageEl?.getBoundingClientRect();
  const topBefore = rect ? rect.top + window.scrollY : 0;
  const relCenter = rect ? (window.scrollY + window.innerHeight / 2 - topBefore) / rect.height : 0.5;

  setZoom(next);

  // after React applies new sizes, restore scroll to the same relative center
  requestAnimationFrame(() => {
    const rect2 = pageEl?.getBoundingClientRect();
    if (!rect2) return;
    const topAfter = rect2.top + window.scrollY;
    const targetScroll = topAfter + rect2.height * relCenter - window.innerHeight / 2;
    window.scrollTo({ top: targetScroll, behavior: 'auto' });
  });
};

const zoomIn    = () => zoomTo(zoomLevel + 0.1);
const zoomOut   = () => zoomTo(zoomLevel - 0.1);
const zoomReset = () => zoomTo(1);

  return (
    <div className={`book-reader-page ${ (showComments || showNotes) ? 'with-left-sidebar' : ''}`}>
      {/* drawing toolbar — static sticky at top */}
      {caps.drawing && (
        <div className="toolbar-wrapper toolbar--horizontal">
          <DrawingToolbar
            activeTool={activeTool}
            onToolSelect={onToolSelect}
            currentColor={color}
            setCurrentColor={setColor}
            quotePicking={quotePicking}

            penWidth={penWidth}
            highlightWidth={highlightWidth}
            eraserWidth={eraserWidth}
            eraserStyle={eraserStyle}
            highlightMode={highlightMode}

            onPenWidthChange={setPenWidth}
            onHighlightWidthChange={setHighlightWidth}
            onEraserConfigChange={({ style, width }) => {
              let changed = false;
              if (style) { setEraserStyle(style); changed = true; }
              if (typeof width === 'number') { setEraserWidth(width); eraserWidthRef.current = width; changed = true; }
              if (changed && activeTool === 'eraser') {
                const f = fabricCanvasEl.current?.fabric;
                if (f) { clearTransientHandlers(f); }
                onToolSelect('eraser');
              }
            }}
            onHighlightModeChange={(mode) => {
              setHighlightMode(mode);
              if (mode === 'text' && activeTool === 'highlight') {
                setActiveTool(null);
                beginNativeQuotePicking();
              }
            }}
            onUndo={doUndo}
            onRedo={doRedo}
            canUndo={canUndo}
            canRedo={canRedo}
          />
        </div>
      )}

      {/* bottom-center controls — zoom + page navigation */}
      <div className="controls-row controls-bottom-fixed bottom-center-controls">
        <div className="controls-bar controls-transparent">
          <div className="zoom-controls-wrapper">
            <ZoomControls
              zoomLevel={zoomLevel}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onReset={zoomReset}
            />
          </div>

          <div className="pdf-controls">
            <PageControls
              currentPage={pageNumber}
              totalPages={numPages}
              onPrev={async () => {
                const f = fabricCanvasEl.current?.fabric;
                if (f) await flushSave();
                setPage((p) => Math.max(1, p - 1));
                window.scrollTo({ top: 0, behavior: "auto" });
              }}
              onNext={async () => {
                const f = fabricCanvasEl.current?.fabric;
                if (f) await flushSave();
                setPage((p) => Math.min(numPages, p + 1));
                window.scrollTo({ top: 0, behavior: "auto" });
              }}
            />
          </div>

        </div>
      </div>

      {/* controlled sidebars */}
      <CommentSidebar
        open={caps.comments && showComments}
        onClose={() => { if (window.__quotePicking) endQuotePicking(); setShowComments(false); }}
        currentPage={pageNumber}
        bookId={rid}
        itemType={inferredKind}
        onBeginQuotePicking={beginNativeQuotePicking}
        onPickQuote={pickOneQuoteSelection}
        onEndQuotePicking={endQuotePicking}
        onRegisterQuoteAppend={registerCommentQuoteAppend}
        onComposeOpen={onComposeOpen}
        onComposeClose={onComposeClose}
        showComposer={showCommentComposer}
        onToggleComposer={setShowCommentComposer}
        onSnapshot={setCommentsSnapshot}
        // Normalized the same way EditorPage does it. Without this the reader
        // passed nothing, so CommentCard's `isOwner` was false for every
        // comment here and the owner-only actions were invisible to their own
        // authors — which read as "the reader just doesn't have Edit".
        currentUserId={user?._id || user?.id || user?.auth_sub || null}
        isAdmin={isAdminViewer}
        // On a published book, being able to read a comment at all already
        // means owning it: comments.py gates book reads on `user_owns_book`.
        // EXCEPT for an admin, who clears that read check through the blanket
        // admin branch without owning anything -- so inferring ownership from
        // readership shows them "Add tags" on a book they do not own, and the
        // server now answers 403. Admins therefore fall back to false and tag
        // from the editor. A draft opened here does not load its author, so it
        // stays false too.
        isItemOwner={inferredKind === 'book' && !isAdminViewer}
      />
      <NotesSidebar
        open={caps.notes && showNotes}
        onClose={() => { if (window.__quotePicking) endQuotePicking(); setShowNotes(false); }}
        bookId={rid}
        currentPage={pageNumber}
        onBeginQuotePicking={beginNativeQuotePicking}
        onPickQuote={pickOneQuoteSelection}
        onEndQuotePicking={endQuotePicking}
        onRegisterQuoteAppend={registerNoteQuoteAppend}
      />

      {/* centered page sheet */}
      <div className="content-row" ref={shellRef}>
        <div className="page-box" ref={pageBoxRef} onMouseDown={handlePanStart}>
          <div className="page-content" ref={pageContentRef}>
            <canvas ref={pdfCanvasRef} className="pdf-canvas" />
            <div ref={textLayerRef} className="textLayer" />
            <div ref={heatmapLayerRef} className={`heatmap-layer ${showComments ? '' : 'hidden'}`} />

            <div
              ref={marqueeLayerRef}
              className={`marquee-layer${marqueeActive ? " active" : ""}`}
            />

            <canvas ref={fabricCanvasEl} className="fabric-canvas" />
          </div>

        
          {selectionMenu.open && (
            <div
              className="quote-menu"
              style={{
                position: 'absolute',
                left: `${selectionMenu.x}px`,
                top: `${selectionMenu.y}px`,
                zIndex: 9,
                background: '#fff',
                border: '1px solid #dadce0',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,.12)',
                padding: 6,
                minWidth: 160,
              }}
            >
              <button
                type="button"
                onClick={onPasteQuoteFromMenu}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                Paste to Quoted Passage
              </button>
              <button
                type="button"
                onClick={onUnselectFromMenu}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                Unselect text
              </button>
            </div>
          )}
        </div>
      </div>
          <div className="reader-scroll-spacer" aria-hidden="true" />

      {!showComments && !quotePicking && (
        <button
          type="button"
          className="float-comment-btn"
          onClick={onCursorCommentClick}
          title="Write public comment"
          aria-label="Write public comment"
        >
          Write a comment
        </button>
      )}
      {!showNotes && !quotePicking && (
        <button
          type="button"
          className="float-comment-btn float-note-btn"
          onClick={() => {
            setShowNotes(true);
            beginNativeQuotePicking(); // optional
          }}
          title="Write personal note"
          aria-label="Write personal note"
        >
          Write notes
        </button>
      )}

      {/* bottom-left FABs that open panels directly – hidden while a sidebar is open */}
      {!showComments && !showNotes && (
        <div className="reader-fabs">
          {caps.comments && (
            <button
              className="fab"
              onClick={() => setShowComments(true)}
              aria-label="Open comments"
              title="Comment Panel"
              type="button"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  d="M4 4h16v10H8l-4 4V4z"
                  fill="#b39ddb"
                  stroke="#7e57c2"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {caps.notes && (
            <button
              className="fab"
              onClick={() => setShowNotes(true)}
              aria-label="Open notes"
              title="Notes Panel"
              type="button"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path d="M4 3h12v18H4z" fill="#eceff1" stroke="#90a4ae" />
                <path d="M14 4l6 6-8 8H6v-6z" fill="#ffcc80" stroke="#fb8c00" />
                <path d="M15 5l4 4" stroke="#6d4c41" strokeWidth="2" />
              </svg>
            </button>
          )}
        </div>
      )}


      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", minHeight: "30vh" }}>
          <CircularProgress size={60} />
        </Box>
      )}
      {loadError && (
        <Box sx={{ p: "2rem", textAlign: "center" }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {loadError}
          </Typography>
          {passedBook?.title && (
            <Typography variant="body1">Failed to load: {passedBook.title}</Typography>
          )}
        </Box>
      )}
    </div>
  );
}
